import { Flex, type FlexProps, type TextProps } from "@mantine/core";
import dayjs from "dayjs";
import { type CSSProperties, type ReactNode } from "react";
import { ReviewText } from "src/components/pages/Summary/ReviewsTable/ReviewsTable.styled";

interface DateTimeDispProps {
  utc?: string | null;
  utcOffset?: string;
  lt?: string | null;
  dir?: "row" | "column";
  date?: boolean;
  time?: boolean;
  color?: string;
  size?: string | number;
  disabled?: boolean;
  containerProps?: FlexProps;
  textProps?: TextProps;
  mergeDate?: boolean;
  gap?: number;
  withSeconds?: boolean;
}

const DateTimeDisp = ({
  utc,
  utcOffset,
  lt,
  dir = "column",
  date = true,
  time = true,
  color,
  size,
  disabled,
  textProps = {},
  containerProps = {},
  mergeDate = true,
  gap,
  withSeconds = false,
}: DateTimeDispProps) => {
  const utcDate =
    utc && dayjs(utc).isValid() ? dayjs.tz(utc, "Etc/GMT-0") : undefined;
  const ltDate = lt && dayjs(lt).isValid() ? dayjs(lt) : undefined;

  if (mergeDate && utcDate && ltDate) {
    mergeDate = utcDate.isSame(ltDate, "day");
    dir = mergeDate ? "row" : dir;
  } else {
    mergeDate = false;
  }

  const format = `DD MMM YYYY - HH:mm${withSeconds ? ":ss" : ""}`;
  utc = utcDate ? utcDate.format(format) : undefined;
  lt = ltDate ? ltDate.format(format) : undefined;

  const utcDateStr = utc?.split(" - ")[0];
  const utcTimeStr = utc?.split(" - ")[1];
  const ltDateStr = lt?.split(" - ")[0];
  const ltTimeStr = lt?.split(" - ")[1];

  const utcDateDisplay = date ? (utcDateStr ?? "") : "";
  const utcTimeDisplay = time
    ? `${utcTimeStr ?? ""} UTC${utcOffset?.replace("null", "") ? ` ${!["+", "-"].some(s => utcOffset.includes(s)) ? "+" : ""}${utcOffset}` : ""}`
    : "";

  const utcDateChunk = `${utcDateDisplay}${date && time ? " " : ""}`;

  const ltDateDisplay = !mergeDate && date ? (ltDateStr ?? "") : "";
  const ltTimeDisplay = time ? `${ltTimeStr ?? ""} LT` : "";
  const ltDateChunk = `${ltDateDisplay}${
    !mergeDate && date && time ? " " : ""
  }`;

  const shouldMergeInRow = utc && lt && dir === "row" && mergeDate;

  const Text = ({
    children,
    style,
  }: {
    children: ReactNode;
    style?: CSSProperties;
  }) => (
    <ReviewText
      disabled={disabled}
      color={color}
      fz={size ?? "inherit"}
      style={style}
      {...textProps}>
      {children}
    </ReviewText>
  );
  return (
    <Flex
      direction={dir}
      align={dir === "row" ? "center" : "flex-start"}
      wrap={dir === "row" ? "wrap" : "nowrap"}
      gap={dir === "row" ? (gap ?? 4) : (gap ?? 2)}
      styles={{ ...containerProps.styles }}
      {...containerProps}>
      {((!utc && !lt) || (!date && !time)) && <Text>No Entry</Text>}
      {shouldMergeInRow ? (
        <>
          {<Text>{utcDateChunk}</Text>}
          {
            <Flex direction="row" gap={4} wrap="inherit">
              <Text>{utcTimeDisplay}</Text> <Text> | </Text>
              <Text>{ltTimeDisplay}</Text>
            </Flex>
          }
        </>
      ) : (
        <>
          {utc && (
            <Flex direction="row" gap={4} wrap="inherit">
              <Text>{utcDateChunk}</Text>
              <Text>{utcTimeDisplay}</Text>
            </Flex>
          )}
          {utc && lt && dir === "row" && <Text> | </Text>}
          {lt && (
            <Flex direction="row" gap={4} wrap="inherit">
              <Text>{ltDateChunk}</Text>
              <Text>{ltTimeDisplay}</Text>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};

export default DateTimeDisp;
